// src/lib/phone.ts — phone numbers, the same rule as the server (src/modules/shared/phone.ts).
// Every phone field (components/ui/PhoneInput) has a country dropdown that defaults to the UK; the number is checked for
// that country with libphonenumber-js and kept as E.164 (+447700900123). UK numbers show the way staff read them
// (07700 900123), other countries' internationally (+91 98765 43210).
import { getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';

export type { CountryCode };
export const DEFAULT_COUNTRY: CountryCode = 'GB';

/** Ofcom's drama / test numbers (07700 900xxx, 01632 960xxx): not "valid" in the metadata, but the forms' example and the seeds use them. */
const OFCOM_FICTION = /^\+44(?:7700900\d{3}|1632960\d{3})$/;

const regionNames = (() => { try { return new Intl.DisplayNames(['en-GB'], { type: 'region' }); } catch { return null; } })();
/** The country's English name (Intl), e.g. "United Kingdom". */
export const countryLabel = (c: string) => regionNames?.of(c) ?? c;
const withThe = (c: string) => (['GB', 'US', 'AE', 'NL', 'PH'].includes(c) ? `the ${countryLabel(c)}` : countryLabel(c));
export const phoneMessage = (c: string = DEFAULT_COUNTRY) => `Enter a valid mobile or landline number for ${withThe(c)}`;
/** 🇬🇧 from GB. */
export const flagOf = (c: string) => String.fromCodePoint(...[...c.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));

export interface CountryOption { code: CountryCode; dial: string; name: string; flag: string }
let options: CountryOption[] | null = null;
/** Every country in the metadata, by name, the UK first. */
export function countryOptions(): CountryOption[] {
  if (!options) {
    const all = getCountries().map((code) => ({ code, dial: `+${getCountryCallingCode(code)}`, name: countryLabel(code), flag: flagOf(code) }));
    all.sort((a, b) => a.name.localeCompare(b.name));
    const gb = all.find((o) => o.code === DEFAULT_COUNTRY)!;
    options = [gb, ...all.filter((o) => o !== gb)];
  }
  return options;
}

const MAIN: Record<string, CountryCode> = { '44': 'GB', '1': 'US', '7': 'RU', '61': 'AU', '39': 'IT', '47': 'NO', '358': 'FI', '590': 'GP', '262': 'RE', '599': 'CW', '212': 'MA' };
/** The country of a number written with its + code (its own region when the metadata knows it, else the code's main country). */
export function countryOfValue(v: string | null | undefined): CountryCode | null {
  const s = String(v ?? '').trim().replace(/^00(?=[1-9])/, '+');
  if (!s.startsWith('+')) return null;
  const p = parsePhoneNumberFromString(s);
  if (!p) return null;
  return p.country ?? MAIN[p.countryCallingCode] ?? getCountries().find((c) => getCountryCallingCode(c) === p.countryCallingCode) ?? null;
}

/** E.164 for the national part typed with the chosen country ('' when nothing is typed). */
export function phoneValueOf(national: string, country: CountryCode): string {
  const digits = national.replace(/\D/g, '');
  if (!digits) return '';
  const p = parsePhoneNumberFromString(national, country);
  return p?.number ?? `+${getCountryCallingCode(country)}${digits.replace(/^0/, '')}`;
}

/** The stored number split for the field: its country and the national part as written in that country. */
export function splitPhone(v: string | null | undefined, fallback: CountryCode = DEFAULT_COUNTRY): { country: CountryCode; national: string } {
  const s = String(v ?? '').trim();
  if (!s) return { country: fallback, national: '' };
  const country = countryOfValue(s);
  if (!country) {
    const p = parsePhoneNumberFromString(s, fallback);
    return { country: fallback, national: p ? p.formatNational() : s };
  }
  const p = parsePhoneNumberFromString(s.replace(/^00/, '+'));
  return { country, national: p ? p.formatNational() : s.replace(/^\+\d+\s*/, '') };
}

/** A complete, valid number (E.164 or national for `country`). */
export function phoneOk(v: string | null | undefined, country: CountryCode = DEFAULT_COUNTRY): boolean {
  const s = String(v ?? '').trim();
  if (!s) return false;
  const e164 = parsePhoneNumberFromString(s.replace(/^00(?=[1-9])/, '+'), country)?.number;
  return (!!e164 && OFCOM_FICTION.test(e164)) || isValidPhoneNumber(s.replace(/^00(?=[1-9])/, '+'), country);
}
/** The message for a number that is not valid, naming its country. */
export const phoneError = (v: string | null | undefined) => phoneMessage(countryOfValue(v) ?? DEFAULT_COUNTRY);

/** How a phone number is shown: UK numbers nationally (07700 900123), others internationally (+91 98765 43210). */
export function formatPhone(v: string | null | undefined): string {
  if (!v) return '';
  const s = String(v).trim().replace(/^00(?=[1-9])/, '+');
  const p = parsePhoneNumberFromString(s, DEFAULT_COUNTRY);
  if (!p) return String(v);
  return p.countryCallingCode === '44' ? p.formatNational() : p.formatInternational();
}
